import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFilter } from 'react-icons/fa';
import U2000ComboBoxBase from '../../u2000Core/components/formControls/combobox/U2000ComboBoxBase';
import type {
  U340xKpiDetailColumnDef,
  U340xKpiDetailColumnFormat,
  U340xKpiMonthDetailRow,
  U340xKpiMonthDetailTotals,
} from '../types/u340xKpiLuTypes';
import {
  u340xFilterDetailRowsByBillable,
  u340xFilterDetailRowsByNature,
  u340xFormatTotalForColumn,
  u340xSumDetailTotalsFromRows,
  type U340xKpiDetailBillableFilter,
  type U340xKpiDetailNatureFilter,
} from '../utils/u340xKpiDetailFilter';
import { NS } from '../u340xKpiNs';

type U340xDetailBillableFilterOption = {
  key: Exclude<U340xKpiDetailBillableFilter, 'all'>;
  description: string;
};

type U340xDetailNatureFilterOption = {
  key: Exclude<U340xKpiDetailNatureFilter, 'all'>;
  description: string;
};

export type U340xKpiMonthDetailTableStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export type { U340xKpiDetailBillableFilter, U340xKpiDetailNatureFilter };

export interface U340xKpiMonthDetailTableProps {
  month: string | null;
  columns: U340xKpiDetailColumnDef[];
  rows: U340xKpiMonthDetailRow[];
  status: U340xKpiMonthDetailTableStatus;
  errorMessage?: string;
  /** Totaux API (U3402 / U3403) — affichés en pied de tableau aligné sur les colonnes. */
  totals?: U340xKpiMonthDetailTotals | null;
}

function formatDocumentNature(
  value: string | number | boolean | null | undefined,
  t: (key: string) => string,
): string {
  if (value === 'STANDARD') return t('detail.documentNatureStandard');
  if (value === 'WIP_WRITE_OFF') return t('detail.documentNatureWriteOff');
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function formatCellValue(
  value: string | number | boolean | null | undefined,
  format: U340xKpiDetailColumnFormat | undefined,
  t: (key: string) => string,
): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (format === 'boolean') {
    return value === true ? t('detail.yes') : t('detail.no');
  }
  if (format === 'currency' && typeof value === 'number') {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (format === 'number' && typeof value === 'number') {
    return new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 2 }).format(value);
  }
  return String(value);
}

function alignClass(align?: 'left' | 'center' | 'right'): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

function U340xKpiDetailTotalsFooter({
  columns,
  totals,
}: {
  columns: U340xKpiDetailColumnDef[];
  totals: U340xKpiMonthDetailTotals;
}) {
  const { t } = useTranslation(NS);
  const labelColIndex = columns.findIndex(
    (col) => col.key === 'projetName' || col.key === 'invoiceNumber',
  );
  const labelIndex = labelColIndex >= 0 ? labelColIndex : 0;

  return (
    <tfoot className="border-t border-gray-200 bg-gray-50" data-testid="u340x-detail-totals">
      <tr>
        {columns.map((col, index) => {
          const totalValue = u340xFormatTotalForColumn(col.key, totals);
          if (totalValue != null) {
            return (
              <td
                key={col.key}
                className={`whitespace-nowrap px-3 py-2 font-semibold text-gray-900 ${alignClass(col.align)}`}
              >
                {totalValue}
              </td>
            );
          }
          if (index === labelIndex) {
            return (
              <td
                key={col.key}
                className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 ${alignClass(col.align)}`}
              >
                {t('detail.totalsTitle')}
              </td>
            );
          }
          return <td key={col.key} className="px-3 py-2" />;
        })}
      </tr>
    </tfoot>
  );
}

function U340xKpiMonthDetailTable({
  month,
  columns,
  rows,
  status,
  errorMessage,
  totals,
}: U340xKpiMonthDetailTableProps) {
  const { t } = useTranslation(NS);
  const [billableFilter, setBillableFilter] = useState<U340xKpiDetailBillableFilter>('all');
  const [natureFilter, setNatureFilter] = useState<U340xKpiDetailNatureFilter>('all');
  const showBillableFilter = columns.some((col) => col.key === 'billable');
  const showNatureFilter = columns.some((col) => col.key === 'documentNature');

  useEffect(() => {
    setBillableFilter('all');
    setNatureFilter('all');
  }, [month]);

  const billableFilterOptions = useMemo<U340xDetailBillableFilterOption[]>(
    () => [
      { key: 'billable', description: t('detail.filterBillable') },
      { key: 'nonBillable', description: t('detail.filterNonBillable') },
    ],
    [t],
  );

  const natureFilterOptions = useMemo<U340xDetailNatureFilterOption[]>(
    () => [
      { key: 'standard', description: t('detail.filterStandard') },
      { key: 'writeOff', description: t('detail.filterWriteOff') },
    ],
    [t],
  );

  const selectedBillableOption = useMemo(
    () => billableFilterOptions.find((option) => option.key === billableFilter) ?? null,
    [billableFilterOptions, billableFilter],
  );

  const selectedNatureOption = useMemo(
    () => natureFilterOptions.find((option) => option.key === natureFilter) ?? null,
    [natureFilterOptions, natureFilter],
  );

  const filteredRows = useMemo(() => {
    if (showBillableFilter) {
      return u340xFilterDetailRowsByBillable(rows, billableFilter);
    }
    if (showNatureFilter) {
      return u340xFilterDetailRowsByNature(rows, natureFilter);
    }
    return rows;
  }, [rows, showBillableFilter, showNatureFilter, billableFilter, natureFilter]);

  const activeFilterIsAll = showBillableFilter
    ? billableFilter === 'all'
    : showNatureFilter
      ? natureFilter === 'all'
      : true;

  const displayTotals = useMemo(() => {
    if (!totals && activeFilterIsAll) return null;
    if (activeFilterIsAll) return totals ?? null;
    return u340xSumDetailTotalsFromRows(filteredRows);
  }, [totals, activeFilterIsAll, filteredRows]);

  const showTotals = Boolean(displayTotals) && (status === 'ready' || status === 'empty');
  const showFilteredEmpty = status === 'ready' && filteredRows.length === 0;
  const showTableBody = status === 'ready' && filteredRows.length > 0;
  const showTableShell =
    status === 'ready' || (status === 'empty' && showTotals);

  if (!month || status === 'idle') {
    return (
      <div className="rounded-sm border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-muted-foreground">
        {t('standalone.detailHint')}
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-800">
          {t('standalone.detailTitle', { month })}
        </h3>
        {showBillableFilter && (status === 'ready' || status === 'empty') ? (
          <div
            className="flex min-w-48 max-w-xs items-center gap-2"
            data-testid="u340x-detail-billable-filter"
          >
            <FaFilter className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
            <div className="min-w-0 flex-1">
              <U2000ComboBoxBase<U340xDetailBillableFilterOption>
                data={billableFilterOptions}
                keyField="key"
                descField="description"
                value={selectedBillableOption}
                onChange={(key) =>
                  setBillableFilter((key as U340xKpiDetailBillableFilter | null) ?? 'all')
                }
                noneItemLabel={t('detail.filterAll')}
                placeHolder={t('detail.filterBillableLabel')}
              />
            </div>
          </div>
        ) : null}
        {showNatureFilter && (status === 'ready' || status === 'empty') ? (
          <div
            className="flex min-w-48 max-w-xs items-center gap-2"
            data-testid="u340x-detail-nature-filter"
          >
            <FaFilter className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
            <div className="min-w-0 flex-1">
              <U2000ComboBoxBase<U340xDetailNatureFilterOption>
                data={natureFilterOptions}
                keyField="key"
                descField="description"
                value={selectedNatureOption}
                onChange={(key) =>
                  setNatureFilter((key as U340xKpiDetailNatureFilter | null) ?? 'all')
                }
                noneItemLabel={t('detail.filterNatureAll')}
                placeHolder={t('detail.filterNatureLabel')}
              />
            </div>
          </div>
        ) : null}
      </div>

      {status === 'loading' ? (
        <div
          className="flex h-40 flex-col justify-center gap-3 px-4"
          aria-busy="true"
          aria-label={t('status.loading')}
        >
          <div className="h-3 w-1/4 animate-pulse rounded-sm bg-gray-200" />
          <div className="h-24 w-full animate-pulse rounded-sm bg-gray-100" />
        </div>
      ) : null}

      {status === 'error' ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm font-medium text-red-700">{t('status.error')}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {errorMessage ?? t('standalone.detailError')}
          </p>
        </div>
      ) : null}

      {status === 'empty' && !showTotals ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t('standalone.detailEmpty')}
        </div>
      ) : null}

      {showFilteredEmpty ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t('detail.filterEmpty')}
        </div>
      ) : null}

      {showTableShell ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`px-3 py-2 font-medium text-gray-600 ${alignClass(col.align)}`}
                  >
                    {t(col.labelKey)}
                  </th>
                ))}
              </tr>
            </thead>
            {showTableBody ? (
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`whitespace-nowrap px-3 py-2 text-gray-800 ${alignClass(col.align)}`}
                      >
                        {col.key === 'documentNature'
                          ? formatDocumentNature(row[col.key], t)
                          : formatCellValue(row[col.key], col.format, t)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : null}
            {showTotals && displayTotals ? (
              <U340xKpiDetailTotalsFooter columns={columns} totals={displayTotals} />
            ) : null}
          </table>
        </div>
      ) : null}
    </div>
  );
}

export default U340xKpiMonthDetailTable;
